// Svastika theme — base utilities. All interactive sections register web components here.

const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class IntervalRotator extends HTMLElement {
  connectedCallback() {
    const items = Array.from(this.querySelectorAll('[data-rotator-item]'));
    if (items.length < 2) return;
    const interval = parseInt(this.dataset.interval || '4200', 10);
    let i = 0;
    items[0].setAttribute('data-active', '');
    if (isReducedMotion) return;
    this._timer = setInterval(() => {
      items[i].removeAttribute('data-active');
      i = (i + 1) % items.length;
      items[i].setAttribute('data-active', '');
      this.dispatchEvent(new CustomEvent('rotator:change', { detail: { index: i } }));
    }, interval);
  }
  disconnectedCallback() { clearInterval(this._timer); }
}
customElements.define('svastika-rotator', IntervalRotator);

class Disclosure extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-disclosure-trigger]');
      if (!trigger) return;
      const item = trigger.closest('[data-disclosure-item]');
      const open = item.hasAttribute('data-open');
      if (this.dataset.exclusive !== 'false') {
        this.querySelectorAll('[data-disclosure-item][data-open]').forEach(el => el.removeAttribute('data-open'));
      }
      if (!open) item.setAttribute('data-open', '');
      else item.removeAttribute('data-open');
    });
  }
}
customElements.define('svastika-disclosure', Disclosure);

/* svstk-7q2x9k3 — "first paint settled" gate shared by every <svastika-shop-video>.
   Resolves on the FIRST of: window 'load' (LCP + critical resources done) OR the first user
   intent (scroll / pointerdown / keydown / touchstart). No video element creates its
   IntersectionObserver or attaches a src until this resolves, so a card video just below the
   fold can never compete with the LCP image during initial load. */
const svstkVideoReady = new Promise((resolve) => {
  if (document.readyState === 'complete') { resolve(); return; }
  let done = false;
  const fire = () => {
    if (done) return; done = true;
    window.removeEventListener('load', fire);
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((t) => window.removeEventListener(t, fire, { passive: true }));
    resolve();
  };
  window.addEventListener('load', fire, { once: true });
  ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((t) => window.addEventListener(t, fire, { passive: true }));
});

/* svastika-shop-video — lazy, muted, in-view autoplay video. Wraps a <video preload="none" data-src>;
   attaches src + plays only when near the viewport, pauses when out. Zero video bytes until in view.
   Used by product cards (complementary) and the craft section.
   Opt-in data-mode="image-first" (svstk-7q2x9k3): element holds BOTH an <img> (visible) and the
   <video data-src> (CSS-layered opacity:0 over the image); on in-view the video buffers in the
   background and the element gains .is-video-ready so the card's CSS crossfades it in. Reduced-motion
   users never download the video; out-of-view keeps src so it resumes without re-fetching.
   Loading is additionally gated behind svstkVideoReady (above) so nothing fetches until first
   paint has settled (window load) or the user shows intent (scroll/pointer). */
class SvastikaShopVideo extends HTMLElement {
  connectedCallback() {
    const v = this.querySelector('video');
    if (!v) return;
    const imageFirst = this.dataset.mode === 'image-first';
    const attach = () => { if (!v.getAttribute('src') && v.dataset.src) { v.setAttribute('src', v.dataset.src); try { v.load(); } catch (e) {} } };
    /* svstk-7q2x9k3 — parked poster. A <video poster> is fetched at first layout even under
       preload="none" (preload governs the MEDIA stream only), so callers where the poster genuinely
       IS the visible frame park it in data-ph-poster and we attach it here — on the same in-view
       tick the media is decided, never earlier. Deliberately runs for BOTH modes and BEFORE the
       image-first reduced-motion early return: those users never load the video, so the poster is
       the only frame they get. No-op when data-ph-poster is absent (product-card / collection-card /
       premium-feature emit no poster at all), and one-shot — the attribute is removed on attach. */
    const attachPoster = () => {
      const ph = v.dataset.phPoster;
      if (!ph) return;
      v.poster = ph;
      v.removeAttribute('data-ph-poster');
    };
    // image-first: buffer in the background, crossfade in on loadeddata; honour reduced-motion.
    const enter = () => {
      attachPoster();
      if (imageFirst) {
        const video = this.querySelector('video[data-src]');
        if (video && !video.src) {
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; // keep image only, never load video
          video.src = video.dataset.src;
          video.addEventListener('loadeddata', () => { this.classList.add('is-video-ready'); video.play().catch(() => {}); }, { once: true });
          video.addEventListener('error', () => { video.remove(); }, { once: true });
        } else if (video) {
          video.play().catch(() => {});
        }
        return;
      }
      attach(); const p = v.play(); if (p && p.catch) p.catch(() => {});
    };
    // Wait for first paint to settle (or user intent) before observing — keeps video bytes
    // off the critical path so they never compete with the LCP image. _dead guards against an
    // element disconnected during the await.
    svstkVideoReady.then(() => {
      if (this._dead) return;
      if (!('IntersectionObserver' in window)) { enter(); return; }
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) { enter(); }
          else { v.pause(); } // image-first: pause only — keep src so it resumes without re-fetching.
        });
      }, { rootMargin: '200px 0px' });
      this._io.observe(this);
    });
  }
  disconnectedCallback() { this._dead = true; if (this._io) this._io.disconnect(); }
}
if (!customElements.get('svastika-shop-video')) customElements.define('svastika-shop-video', SvastikaShopVideo);

/* svastika-wishlist — localStorage "kept close" list. Display-only, syncable later. SVSTK */
class SvastikaWishlist extends HTMLElement {
  static KEY = 'svstk:kept-close';
  static read() { try { return JSON.parse(localStorage.getItem(SvastikaWishlist.KEY) || '[]'); } catch { return []; } }
  static write(ids) { try { localStorage.setItem(SvastikaWishlist.KEY, JSON.stringify(ids)); } catch {} }
  static has(id) { return SvastikaWishlist.read().includes(String(id)); }
  static toggle(id) {
    id = String(id);
    const ids = SvastikaWishlist.read();
    const i = ids.indexOf(id);
    if (i >= 0) ids.splice(i, 1); else ids.push(id);
    SvastikaWishlist.write(ids);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { id, saved: i < 0, count: ids.length } }));
    return i < 0;
  }
  connectedCallback() {
    this.hydrate();
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      const saved = SvastikaWishlist.toggle(btn.dataset.wish);
      this.paint(btn, saved);
    });
    this._onChange = () => { this.hydrate(); this.syncCount(); };
    document.addEventListener('wishlist:change', this._onChange);
    this.syncCount();
  }
  disconnectedCallback() { if (this._onChange) document.removeEventListener('wishlist:change', this._onChange); }
  hydrate() {
    const set = new Set(SvastikaWishlist.read().map(String));
    this.querySelectorAll('[data-wish]').forEach((b) => this.paint(b, set.has(String(b.dataset.wish))));
  }
  paint(btn, saved) { btn.setAttribute('aria-pressed', saved ? 'true' : 'false'); btn.classList.toggle('is-saved', saved); }
  syncCount() {
    const n = SvastikaWishlist.read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = String(n); el.hidden = n === 0; });
  }
}
if (!customElements.get('svastika-wishlist')) customElements.define('svastika-wishlist', SvastikaWishlist);

window.SvastikaTheme = { isReducedMotion };
